import React, { useMemo } from "react";
import PropTypes from "prop-types";
import CatalogProductCardSkeleton from "./CatalogProductCardSkeleton";
import { resolveCatalogProductSkeletonPlan } from "./resolve-catalog-product-skeleton-plan";
import sliderStyles from "../../../layouts/product-recommendation/components/ProductRecommendationSlider.module.css";

export default function CatalogProductOfferSkeleton({
  section,
  popupData,
  isMobile = false,
  count = 3,
  layoutProfile = "recommendation",
}) {
  const plan = useMemo(
    () =>
      resolveCatalogProductSkeletonPlan({
        section,
        popupData,
        isMobile,
        count,
        layoutProfile,
      }),
    [section, popupData, isMobile, count, layoutProfile]
  );

  const cardProps = {
    isHorizontal: plan.isHorizontal,
    isMobile,
    layoutProfile: plan.layoutProfile,
    mobileCardDensity: plan.mobileCardDensity,
    imageSlotHeight: plan.imageSlotHeight,
    imageColStyle: plan.imageColStyle,
  };

  const cards = Array.from({ length: plan.safeCount }, (_, index) => (
    <CatalogProductCardSkeleton key={index} {...cardProps} />
  ));

  if (layoutProfile === "ai-upsell") {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Loading product recommendations"
        style={{ width: "100%" }}
      >
        {cards[0]}
      </div>
    );
  }

  if (plan.useDesktopGrid || plan.useMobileTwinGrid) {
    const gridClass = plan.useMobileTwinGrid
      ? `${sliderStyles.gridRow} ${sliderStyles.gridRowTwo} ${sliderStyles.mobileTwinGrid}`
      : plan.safeCount === 2
        ? `${sliderStyles.gridRow} ${sliderStyles.gridRowTwo}`
        : sliderStyles.gridRow;

    return (
      <div
        className={`${sliderStyles.sliderWrap} ${sliderStyles.sliderWrapGrid}`}
        role="status"
        aria-live="polite"
        aria-label="Loading product recommendations"
      >
        <div className={gridClass}>
          {cards.map((card, index) => (
            <div key={index} className={sliderStyles.gridCol}>
              {card}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!plan.useSwiper) {
    return (
      <div
        className={`${sliderStyles.singleCard} ${
          isMobile ? sliderStyles.singleCardMobile : ""
        }`.trim()}
        role="status"
        aria-live="polite"
        aria-label="Loading product recommendations"
      >
        {cards[0]}
      </div>
    );
  }

  const slideWidth =
    isMobile && plan.slidesPerView > 1
      ? `calc((100% - 8px) / ${plan.slidesPerView})`
      : `${100 / Math.min(plan.slidesPerView, plan.safeCount)}%`;

  return (
    <div
      className={sliderStyles.sliderWrap}
      role="status"
      aria-live="polite"
      aria-label="Loading product recommendations"
    >
      <div className={sliderStyles.sliderInner}>
        <div
          style={{
            display: "flex",
            gap: isMobile ? 8 : 12,
            width: "100%",
            alignItems: "stretch",
          }}
        >
          {cards.map((card, index) => (
            <div
              key={index}
              style={{
                flex: `0 0 ${slideWidth}`,
                maxWidth: slideWidth,
                minWidth: 0,
                display: "flex",
              }}
            >
              {card}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

CatalogProductOfferSkeleton.propTypes = {
  section: PropTypes.object,
  popupData: PropTypes.object,
  isMobile: PropTypes.bool,
  count: PropTypes.number,
  layoutProfile: PropTypes.oneOf(["recommendation", "ai-upsell"]),
};
